/*
Copyright 2022-2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { type FC, useState, type ComponentProps } from "react";
import { useTranslation } from "react-i18next";
import { Menu, MenuItem, Button as CpdButton } from "@vector-im/compound-web";
import {
  ShareIcon,
  OverflowHorizontalIcon,
  ComputerIcon,
  SettingsSolidIcon,
} from "@vector-im/compound-design-tokens/assets/web/icons";

import RobotIcon from "../icons/Robot.svg?react";
import StopCallRecordingIcon from "../icons/StopCallRecording.svg?react";
import { type UseCallRecordingResult } from "../callrecorder";
import { type UseProtocolistRecordingResult } from "../protocolist";
import styles from "./MoreOptionsMenuButton.module.css";

interface MoreOptionsMenuButtonProps
  extends Omit<ComponentProps<typeof CpdButton>, "onClick" | "iconOnly" | "Icon" | "kind"> {
  recorder: UseCallRecordingResult;
  protocolist: UseProtocolistRecordingResult;
  canScreenshare: boolean;
  onShareClick: (() => void) | null;
  onSettingsClick: (() => void) | null;
}

export const MoreOptionsMenuButton: FC<MoreOptionsMenuButtonProps> = ({
  className,
  recorder,
  protocolist,
  canScreenshare,
  onShareClick,
  onSettingsClick,
  ...props
}) => {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const showProtocolistIndicator = protocolist.viewState === "active";

  const protocolistLabel = (() : string => {
    switch (protocolist.buttonState.kind) {
      case "start":
        return t("call_recorder.start_server_protocol_label");
      case "connecting":
        return t("protocolist.connecting_label");
      case "active_self_stop":
        return t("call_recorder.stop_server_protocol_label");
      case "active_other_locked":
        return t("protocolist.active_label");
      case "stopping":
        return t("protocolist.stopping_label");
    }
  })();

  const onProtocolistSelect = protocolist.buttonState.kind === "start"
    ? (): void => {
        void protocolist.start();
      }
    : protocolist.buttonState.kind === "active_self_stop"
      ? (): void => {
          void protocolist.stop();
        }
      : null;

  return (
    <Menu
      title={t("call_recorder.menu")}
      showTitle={false}
      align="center"
      side="top"
      open={menuOpen}
      onOpenChange={setMenuOpen}
      trigger={
        <div className={styles.triggerWrapper}>
          <CpdButton
            className={className}
            iconOnly
            Icon={OverflowHorizontalIcon}
            kind="secondary"
            aria-label={t("call_recorder.menu")}
            {...props}
          />
          {showProtocolistIndicator && (
            <span className={styles.serverRecordingDot} aria-hidden />
          )}
        </div>
      }
    >
      <MenuItem
        Icon={
          protocolist.buttonState.kind === "active_self_stop"
            ? StopCallRecordingIcon
            : RobotIcon
        }
        label={protocolistLabel}
        onSelect={onProtocolistSelect}
        kind={
          protocolist.buttonState.kind === "active_self_stop"
            ? "critical"
            : "primary"
        }
        hideChevron
        disabled={protocolist.buttonState.disabled}
      />
      {protocolist.canConfigureRecipients && (
        <MenuItem
          Icon={SettingsSolidIcon}
          label={t("protocolist.configure_label")}
          onSelect={protocolist.openRecipientsModal}
          hideChevron
        />
      )}
      {canScreenshare && (
        <MenuItem
          Icon={recorder.isLocalRecording ? StopCallRecordingIcon : ComputerIcon}
          label={
            recorder.isLocalRecording
              ? t("call_recorder.stop_local_label")
              : t("call_recorder.start_local_label")
          }
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onSelect={
            recorder.isLocalRecording
              ? recorder.stopLocalRecording
              : recorder.startLocalRecording
          }
          kind={recorder.isLocalRecording ? "critical" : "primary"}
          hideChevron
          disabled={recorder.isLocalPreparing}
        />
      )}
      {onShareClick && (
        <MenuItem
          Icon={ShareIcon}
          label={t("share_call_link")}
          onSelect={onShareClick}
          hideChevron
        />
      )}
      {onSettingsClick && (
        <MenuItem
          Icon={SettingsSolidIcon}
          label={t("common.settings")}
          onSelect={onSettingsClick}
          hideChevron
        />
      )}
    </Menu>
  );
};
